import { tosAccelerate, isSignedTosUrl } from './tosAccelerate';

/**
 * Append TOS image resize parameters to generate a thumbnail URL.
 *
 * Only applies to Volcano Engine TOS URLs (*.volces.com).
 * Non-TOS URLs are returned unchanged.
 * drama-res regional hosts are rewritten to the transfer-acceleration host.
 * Pre-signed URLs pass through untouched (host/query are part of the signature).
 * Requests the given display width as-is (no Retina multiplier).
 *
 * @see https://www.volcengine.com/docs/6349/153626
 */
export function tosThumbnail(url: string, width: number, height?: number): string {
  if (!url) return url;

  // Signed URL: do not swap host or append processing params (would break signature)
  if (isSignedTosUrl(url)) return url;

  // Rewrite drama-res regional host → accelerate host (before x-tos-process short-circuit)
  url = tosAccelerate(url);

  // Only process TOS URLs
  if (!url.includes('volces.com')) return url;

  // Don't double-process
  if (url.includes('x-tos-process')) return url;

  const w = width ? Math.round(width) : undefined;
  const h = height ? Math.round(height) : undefined;

  let params = 'image/resize';
  if (w) params += `,w_${w}`;
  if (h) params += `,h_${h}`;
  if (!w && !h) return url;
  params += ',m_lfit';

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}x-tos-process=${params}`;
}

/**
 * Append TOS image resize parameters for large on-screen previews.
 *
 * This keeps lightboxes from downloading original multi-megabyte TOS objects
 * while still requesting enough pixels for wide/retina displays.
 */
export function tosPreview(url: string, width = 1200): string {
  return tosThumbnail(url, width);
}

/**
 * Append TOS image resize + watermark parameters to generate a share-preview URL.
 *
 * Only applies to Volcano Engine TOS URLs (*.volces.com).
 * Non-TOS URLs are returned unchanged.
 * Requests the given display width as-is (no Retina multiplier).
 * Adds a semi-transparent diagonal "VIBE PICTURES" watermark.
 *
 * @see https://www.volcengine.com/docs/6349/153626
 */
const WATERMARK_TEXT_B64 = 'VklCRSBQSUNUVVJFUw';

export function tosShareImage(url: string, width = 375): string {
  if (!url) return url;
  if (isSignedTosUrl(url)) return url;
  url = tosAccelerate(url);
  if (!url.includes('volces.com')) return url;
  if (url.includes('x-tos-process')) return url;

  const w = Math.round(width);
  const resize = `image/resize,w_${w},m_lfit`;
  const watermark = `image/watermark,text_${WATERMARK_TEXT_B64},fill_1,rotate_45,color_FFFFFF,t_35,size_30`;

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}x-tos-process=${resize}|${watermark}`;
}
